import React, { useState } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { useContactBulkInviteMutation } from "../../../Services/customer/FrontendCustomer";
import { Box, LinearProgress } from "@mui/material";
import { BULK_UPLOAD_COUNTRY_DIAL_CODES } from "../../../utils/bulkUploadCountryDialCodes";

function getExcelColumnLetter(colIndex: number): string {
  let letter = "";
  let idx = colIndex;
  while (idx > 0) {
    const mod = (idx - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    idx = Math.floor((idx - mod) / 26);
  }
  return letter;
}

interface IProps {
  closeModal: any;
  refetch: any;
  dynamicData: any;
  teamsData: any;
  teamAndAccountData: any;
  tagsData: any;
}

const BulkContact: React.FC<IProps> = ({
  closeModal,
  refetch,
  dynamicData,
  teamsData,
  teamAndAccountData,
  tagsData,
}) => {
  const [selectedCsvFiles, setSelectedCsvFiles] = useState<File[]>([]);
  const [fileToDownload, setFileToDownload] = useState<File | null>(null);
  const [showingLoader, setShowingLoader] = useState(false)
  
  const [failDetails, setFailDetails] = useState<any[]>([]);
  const [progress, setProgress] = React.useState(null);

  const [triggerBulkUpload] = useContactBulkInviteMutation({});

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedCsvFiles(files);
      setFileToDownload(files[0]);
    }
  };

  const handleRemoveCsvFile = (index: number) => {
    const newFiles = Array.from(selectedCsvFiles);
    newFiles.splice(index, 1);
    setSelectedCsvFiles(newFiles);
    if (index === 0 && newFiles.length > 0) {
      setFileToDownload(newFiles[0]); // Update the file to be downloaded if the first file was removed
    } else if (index === 0) {
      setFileToDownload(null); // Clear file to download if no files are left
    }
  };

  const handleCsvDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws: any = workbook.addWorksheet("Contact Worksheet");

    const hiddenSheet = workbook.addWorksheet("DropdownOptions", {
      state: "hidden",
    });

    const countryCodes = BULK_UPLOAD_COUNTRY_DIAL_CODES;
    countryCodes.forEach((country, index) => {
      hiddenSheet.getCell(`A${index + 1}`).value = `${country.iso} (${country.code})`;
    });

    if (teamAndAccountData && teamAndAccountData.length > 0) {
      teamAndAccountData.forEach((team, index) => {
        hiddenSheet.getCell(`C${index + 2}`).value = team?.name;
      });
    }

    const header = [
      "contact_name",
      "contact_email",
      "contact_country_code",
      "contact_phone_number",
      "contact_account",
    ];

    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field) => {
        const csvKey = field.label.replace(/ /g, "_") + 1;
        header.push(`contact_${csvKey}`);
      });
    }

    ws.addRow(header);

    for (let i = 2; i <= 100; i++) {
      ws.addRow([]);
    }

    ws.columns.forEach((col) => {
      col.width = 18;
    });

    const phoneIndex = header.indexOf("contact_phone_number");
    if (phoneIndex !== -1) {
      ws.getColumn(phoneIndex + 1).numFmt = "@";
    }

    const countryCodeIndex = header.indexOf("contact_country_code");
    if (countryCodeIndex !== -1) {
      const columnLetter = getExcelColumnLetter(countryCodeIndex + 1);
      for (let r = 2; r <= 100; r++) {
        ws.getCell(`${columnLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          showErrorMessage: true,
          errorTitle: "Invalid Country Code",
          error: "Please select a valid country code",
          formulae: [`DropdownOptions!$A$1:$A$${countryCodes.length}`],
        };
      }
    }

    dynamicData.forEach((field, index) => {
      const columnIndex = header.length - dynamicData.length + index + 1;
      const columnLetter = String.fromCharCode(64 + columnIndex);

      if (field.field_type === "check-box") {
        const checkboxOptions = field.multiple_choices
          .map((choice) => choice.choice)
          .join(",");

        for (let i = 2; i <= 100; i++) {
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${checkboxOptions}"`],
          };
        }
      } else if (field.field_type === "drop-down") {
        const fieldOptions = field?.choices.map((option: any) => option.choice);

        for (let i = 2; i <= 100; i++) {
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${fieldOptions}"`],
          };
        }
      }
    });

    if (teamAndAccountData && teamAndAccountData.length > 0) {
      const userTeamAndAccountData = `DropdownOptions!$C$2:$C$${teamAndAccountData.length + 1}`;
      const accountColLetter = getExcelColumnLetter(header.indexOf("contact_account") + 1);
      ws.dataValidations.add(`${accountColLetter}2:${accountColLetter}100`, {
        type: "list",
        allowBlank: false,
        formulae: [userTeamAndAccountData],
      });
    }
  
    // Style the header row
    ws.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFADD8E6" },
    };
  
    // Apply styles to all cells
    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = {
          name: "Inter",
          size: 8,
        };
        cell.alignment = {
          horizontal: "center",
        };
      });
    });
  
    // Apply specific red fill style to required headers
    const redHeaders = ["contact_name", "contact_email"];
    redHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName) + 1; // Find column index for each red header
      ws.getCell(1, colIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red color for required headers
      };
    });
  
    // Highlight required dynamic fields
    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex = header.length - dynamicData.length + index + 1; // Adjust for the current dynamic field
          const columnLetter = String.fromCharCode(64 + columnIndex);
          ws.getCell(`${columnLetter}1`).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color for required dynamic fields
          };
        }
      });
    }
  
    // Generate the Excel file as a blob
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );
  
    // Create a link to download the Excel file
    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "Contact_template.xlsx";
    document.body.appendChild(link);
    link.click();
  
    // Clean up
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
  };

  const handleDownloadFailDetails = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Failed Uploads");
  
    // Prepare header for static fields
    let header = [
      "contact_name",
      "contact_email",
      "contact_country_code",
      "contact_phone_number",
      "account",
    ];

    // Dynamically add dynamic field headers
    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field) => {
        const csvKey = field.label.replace(/ /g, "_"); // Replace spaces with underscores
        header.push(`contact_${csvKey}`); // Dynamic field label in header
      });
    }

    // Errors column is added at the last
    header.push("Errors");

    // Add headers to the worksheet
    ws.addRow(header);

    // Set column widths
    ws.columns.map((col) => (col.width = 18));

    const failPhoneIndex = header.indexOf("contact_phone_number");
    if (failPhoneIndex !== -1) {
      ws.getColumn(failPhoneIndex + 1).numFmt = "@";
    }

    // Iterate through failDetails to populate rows
    failDetails.forEach((fail) => {
      const { name, email, account, tags, dynamic_fields, error } = fail;

      const countryCode =
        fail.contact_country_code ??
        dynamic_fields?.contact_country_code ??
        dynamic_fields?.country_code ??
        "";
      const phoneNumber =
        fail.contact_phone_number ??
        dynamic_fields?.contact_phone_number ??
        dynamic_fields?.phone_number ??
        fail.phone_number ??
        "";

      // Prepare dynamic fields data in the same structure as CSV
      const dynamicFieldValues = dynamicData.map((field) => {
        const fieldKey = field.label.replace(/ /g, "_");
        return dynamic_fields[fieldKey] || ""; // Return 'N/A' if value is missing
      });

      // Convert errors into string for easier display in Excel
      const errorString = Array.isArray(error)
        ? error.join(", ")
        : JSON.stringify(error);

      // Apply styles to all cells
      ws.eachRow((row) => {
        row.eachCell((cell) => {
          cell.font = {
            name: "Inter",
            size: 8,
          };
          cell.alignment = {
            horizontal: "center",
          };
        });
      });

      // Add the row with dynamic fields and the error at the end
      ws.addRow([
        name,
        email,
        countryCode,
        phoneNumber,
        account,
        ...dynamicFieldValues, // Spread dynamic field values here
        errorString, // Errors at the end
      ]);
    });
  
    // Apply specific red fill style to headers
    const redHeaders = ["contact_name", "contact_email"];
    redHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName) + 1; // Find column index for each red header
      ws.getCell(1, colIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red color for specific headers
      };
    });
  
    // Apply red fill to required dynamic field headers
    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex = header.indexOf(`contact_${field.label.replace(/ /g, "_")}`) + 1;
          ws.getCell(1, columnIndex).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color for required dynamic fields
          };
        }
      });
    }
  
    // Create the Excel file and initiate the download
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );
  
    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "failed_uploads.xlsx";
    document.body.appendChild(link);
    link.click();
  
    // Clean up temporary URLs and DOM elements
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
    closeModal();
  };
  
  const cleanCsvData = (csvContent: string, percentage: number) => {
    const rows = csvContent.split("\n").map((row) => row.split(","));
    const cleanedRows = rows.filter((row) => row.some((cell) => cell.trim() !== ""));
    const [headers, ...dataRows] = cleanedRows;
  
    const totalRows = Math.ceil((dataRows.length * percentage) / 100);
    const limitedRows = dataRows.slice(0, totalRows);
  
    const cleanedData = limitedRows.map((row) =>
      headers.reduce((acc, header, index) => {
        acc[header.trim()] = row[index]?.trim() || "";
        return acc;
      }, {})
    );
  
    return cleanedData;
  };
  
  const createCsvContent = (data: any[]) => {
    if (data.length === 0) return "";
    const headers = Object.keys(data[0]);
    const rows = data.map((row) => headers.map((header) => row[header]).join(","));
    return [headers.join(","), ...rows].join("\n");
  };

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        return oldProgress;
      });
    }, 500);
  
    return () => {
      clearInterval(timer);
    };
  }, []);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  
    if (selectedCsvFiles.length > 0) {
    setShowingLoader(true)

      const file = selectedCsvFiles[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
  
      const reader = new FileReader();
  
      reader.onload = async (e) => {
        let csvContent: string | undefined;
  
        if (e.target?.result) {
          if (fileExtension === "csv") {
            csvContent = e.target.result as string;
          } else if (fileExtension === "xlsx") {
            const binaryStr = e.target.result as string;
            const workbook = XLSX.read(binaryStr, { type: "binary" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            csvContent = XLSX.utils.sheet_to_csv(worksheet);
          }
  
          if (csvContent) {
            const cleanedData = cleanCsvData(csvContent, 100);
  
            const partSize = Math.ceil(cleanedData.length / 5);
            const dividedData = [];
            for (let i = 0; i < cleanedData.length; i += partSize) {
              dividedData.push(cleanedData.slice(i, i + partSize));
            }
  
            try {
              let combinedFailDetails: any[] = [];
              const totalParts = dividedData.length;
              let allSuccess = false;
  
              for (let i = 0; i < totalParts; i++) {
                const part = dividedData[i];
                const csvContentPart = createCsvContent(part);

                const requestObj = {
                  file_name: `Part_${i + 1}_${file.name}`,
                  csv_file: `data:text/csv;base64,${btoa(csvContentPart)}`,
                };
  
                const response = await triggerBulkUpload({ body: requestObj });

  
                if (
                  !response?.data?.fail_count &&
                  !response?.data?.success_count
                ) {
                  allSuccess = false;
                  break;
                }
  
                if (response) {
                  combinedFailDetails = [
                    ...combinedFailDetails,
                    ...(response?.data?.fail_details || []),
                  ];
                  if (combinedFailDetails?.length > 0) {
                    allSuccess = false

                   }
                   else if (response?.data?.success_count) {
                    allSuccess = true

                   }

                } else {
                }
  
                // Update progress dynamically
                setProgress(((i + 1) / totalParts) * 100);
              }
  
              setFailDetails(combinedFailDetails);
              setSelectedCsvFiles([]);
              refetch();
  
              if (!allSuccess) {
              toast.warning("Some date failed to upload")
              }
              // Display toast based on overall success
              else if (allSuccess) {
                toast.success("File uploaded successfully!");
              } else {
                toast.error("No data found.");
              }
              setShowingLoader(false)
            } catch (error) {
              setShowingLoader(false)

              toast.error("Upload failed. Please try again.");
            }
          }
        }
      };
  
      if (fileExtension === "csv") {
        reader.readAsText(file);
      } else if (fileExtension === "xlsx") {
        reader.readAsBinaryString(file);
      } else {
        toast.error(
          "Unsupported file format. Please upload a CSV or XLSX file."
        );
      }
    }
  };
  
    return (
    <form onSubmit={handleFormSubmit}>
      <div className="mb-4">
        <div className="d-flex justify-content-evenly mt-2 mb-8 mx-1">
          {/* CSV Upload Button */}
          <label
            htmlFor="upload-csv-button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-150px"
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-file-earmark-spreadsheet text-dark fs-4 me-2"></i>
            CSV/XLSX Upload
          </label>
          <input
            type="file"
            id="upload-csv-button"
            accept=".csv, .xlsx"
            style={{ display: "none" }}
            onChange={handleCsvFileChange}
          />

          {/* CSV Download Button */}
          <button
            type="button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-150px"
            onClick={handleCsvDownload}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download text-dark fs-4 me-2"></i>
            Download File
          </button>
        </div>

        {showingLoader ? 

<Box sx={{ width: '100%' }}>
      <LinearProgress variant="determinate" value={progress} />
    </Box>
    : ""
        }

        <div className="mt-3 mb-3">
          <label className="fs-6 fw-bolder mb-2">Important Instructions:</label>
          <br />
          <label className="text-danger">
            &bull; Please upload a valid CSV or XLSX file format only.
          </label>
          <br />
          <label className="text-danger">
            &bull; Headers highlighted in red are mandatory fields.
          </label>
        </div>

<div style={{display: "flex", justifyContent: "center", width: "100%"}}>

        {failDetails?.length > 0 && (
          <button
            type="button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-18 px-3 py-2 rounded mw-150px"
            onClick={handleDownloadFailDetails}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download text-dark fs-4 me-2"></i>
            Error Data
          </button>
        )}
</div>


        {/* Display selected CSV files */}
        {selectedCsvFiles?.length > 0 && (
          <div className="mb-4">
            <h5>Selected CSV files:</h5>
            <ul className="list-group mt-3">
              {selectedCsvFiles.map((file, index) => (
                <li
                  key={index}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="btn btn-link text-danger p-0"
                    onClick={() => handleRemoveCsvFile(index)}
                  >
                    <i className="bi bi-trash text-danger fs-5"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    
   
      <div className="d-flex justify-content-end mb-4 pt-2">
        <button
          type="button"
          className="btn btn-sm btn-white btn-active-light-danger me-2"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-sm btn-primary">
          Submit
        </button>
      </div>
      
    </form>
  );
};

export default BulkContact;